// OfflineLogicSection.js
import React from 'react';
import { Wifi, WifiOff } from 'lucide-react';

const OfflineLogicSection = ({ 
  formData, 
  validationErrors, 
  airtableFields,
  isLoadingFields,
  toggleSection,
  updateOfflineLastModifiedField,
  updateOfflineRecordsList
}) => {
  // Get fields from validate logic for the last modified dropdown
  const validateFields = airtableFields?.validate || [];

  const handleLastModifiedFieldChange = (e) => {
    const fieldId = e.target.value;
    const fieldName = e.target.options[e.target.selectedIndex].text;
    updateOfflineLastModifiedField(fieldId, fieldName);
  };

  const handleRecordsListToggle = () => {
    updateOfflineRecordsList({
      showRecordsList: !formData.offline_logic?.showRecordsList,
      displayFields: formData.offline_logic?.displayFields || [],
      filterConditions: formData.offline_logic?.filterConditions || []
    });
  };

  const handleDisplayFieldToggle = (field) => {
    const currentFields = formData.offline_logic?.displayFields || [];
    const isSelected = currentFields.some(f => f.id === field.id);
    
    let newFields;
    if (isSelected) {
      newFields = currentFields.filter(f => f.id !== field.id);
    } else {
      newFields = [...currentFields, { id: field.id, name: field.name }];
    }
    
    updateOfflineRecordsList({
      showRecordsList: formData.offline_logic?.showRecordsList,
      displayFields: newFields,
      filterConditions: formData.offline_logic?.filterConditions || []
    });
  };

  const handleFilterConditionAdd = () => {
    const newCondition = {
      field: { id: '', name: '' },
      operator: 'is',
      value: '',
      error_message: ''
    };
    
    const currentConditions = formData.offline_logic?.filterConditions || [];
    updateOfflineRecordsList({
      showRecordsList: formData.offline_logic?.showRecordsList,
      displayFields: formData.offline_logic?.displayFields || [],
      filterConditions: [...currentConditions, newCondition]
    });
  };

  const handleFilterConditionUpdate = (index, updatedCondition) => {
    const currentConditions = formData.offline_logic?.filterConditions || [];
    const newConditions = [...currentConditions];
    newConditions[index] = updatedCondition;
    
    updateOfflineRecordsList({
      showRecordsList: formData.offline_logic?.showRecordsList,
      displayFields: formData.offline_logic?.displayFields || [],
      filterConditions: newConditions
    });
  };

  const handleFilterConditionRemove = (index) => {
    const currentConditions = formData.offline_logic?.filterConditions || [];
    const newConditions = currentConditions.filter((_, i) => i !== index);
    
    updateOfflineRecordsList({
      showRecordsList: formData.offline_logic?.showRecordsList,
      displayFields: formData.offline_logic?.displayFields || [],
      filterConditions: newConditions
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium flex items-center">
          <span className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full ${formData.hasOfflineLogic ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>
            {formData.hasOfflineLogic ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />}
          </span>
          Offline Mode
        </h3>
        <div className="relative inline-block w-10 mr-2 align-middle select-none">
          <input
            type="checkbox"
            id="toggle-offline"
            className="sr-only"
            checked={formData.hasOfflineLogic}
            onChange={toggleSection}
          />
          <label 
            htmlFor="toggle-offline"
            className={`block overflow-hidden h-6 rounded-full cursor-pointer ${formData.hasOfflineLogic ? 'bg-orange-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute block h-4 w-4 rounded-full bg-white top-1 transition-transform duration-200 ease-in-out ${formData.hasOfflineLogic ? 'right-1 transform translate-x-0' : 'left-1 transform translate-x-0'}`}></span>
          </label>
        </div>
      </div>
      
      {formData.hasOfflineLogic && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Configure offline mode to allow scanning without internet connectivity. Records will be stored locally and synced when connection is restored.
          </p>

          {!formData.hasValidateLogic && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Offline mode requires Validate Logic to be enabled to determine which records to download for offline use.
                  </p>
                </div>
              </div>
            </div>
          )}

          {formData.hasValidateLogic && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Modified Field
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.offline_logic?.last_modified_field?.id || ''}
                    onChange={handleLastModifiedFieldChange}
                    disabled={isLoadingFields}
                    className={`w-full px-3 py-2 border ${validationErrors.offlineLastModifiedField ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 ${isLoadingFields ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <option value="">
                      {isLoadingFields 
                        ? 'Loading fields...' 
                        : 'Select last modified field'
                      }
                    </option>
                    {validateFields.map(field => (
                      <option key={field.id} value={field.id}>{field.name}</option>
                    ))}
                  </select>
                  {isLoadingFields && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg className="animate-spin h-4 w-4 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                {validationErrors.offlineLastModifiedField && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.offlineLastModifiedField}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  This field will be used to determine which records have been updated since the last sync.
                </p>
              </div>

              {/* Show list of valid records section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Show list of valid records in scanner app</h4>
                    <p className="text-xs text-gray-500">Allow scanner users to browse a list of valid records (e.g., VIP attendees)</p>
                  </div>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="toggle-records-list"
                      className="sr-only"
                      checked={formData.offline_logic?.showRecordsList || false}
                      onChange={handleRecordsListToggle}
                    />
                    <label 
                      htmlFor="toggle-records-list"
                      className={`block overflow-hidden h-6 rounded-full cursor-pointer ${formData.offline_logic?.showRecordsList ? 'bg-orange-500' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute block h-4 w-4 rounded-full bg-white top-1 transition-transform duration-200 ease-in-out ${formData.offline_logic?.showRecordsList ? 'right-1 transform translate-x-0' : 'left-1 transform translate-x-0'}`}></span>
                    </label>
                  </div>
                </div>

                {formData.offline_logic?.showRecordsList && (
                  <div className="space-y-4 ml-4 border-l-2 border-orange-200 pl-4">
                    {/* Display Fields Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fields to display in list
                      </label>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {validateFields.map(field => (
                          <label key={field.id} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={(formData.offline_logic?.displayFields || []).some(f => f.id === field.id)}
                              onChange={() => handleDisplayFieldToggle(field)}
                              className="rounded border-gray-300 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                            />
                            <span className="text-gray-700">{field.name}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Select which fields to show in the records list (e.g., Name, Table Number)
                      </p>
                    </div>

                    {/* Filter Conditions */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Show only select records
                        </label>
                        <button
                          type="button"
                          onClick={handleFilterConditionAdd}
                          className="text-xs bg-orange-500 text-white px-2 py-1 rounded hover:bg-orange-600"
                        >
                          Add Filter
                        </button>
                      </div>
                      
                      {(formData.offline_logic?.filterConditions || []).map((condition, index) => (
                        <div key={index} className="flex items-center space-x-2 mb-2 p-2 bg-gray-50 rounded">
                          {/* Field Selection */}
                          <select
                            value={condition.field?.id || ''}
                            onChange={(e) => {
                              const fieldId = e.target.value;
                              const fieldName = e.target.options[e.target.selectedIndex].text;
                              handleFilterConditionUpdate(index, {
                                ...condition,
                                field: { id: fieldId, name: fieldName }
                              });
                            }}
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                          >
                            <option value="">Select field</option>
                            {validateFields.map(field => (
                              <option key={field.id} value={field.id}>{field.name}</option>
                            ))}
                          </select>

                          {/* Operator Selection */}
                          <select
                            value={condition.operator || 'is'}
                            onChange={(e) => {
                              handleFilterConditionUpdate(index, {
                                ...condition,
                                operator: e.target.value
                              });
                            }}
                            className="px-2 py-1 text-xs border border-gray-300 rounded"
                          >
                            <option value="is">equals</option>
                            <option value="is_not">not equals</option>
                            <option value="contains">contains</option>
                            <option value="does_not_contain">does not contain</option>
                            <option value="is_empty">is empty</option>
                            <option value="is_not_empty">is not empty</option>
                          </select>

                          {/* Value Input */}
                          {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                            <input
                              type="text"
                              value={condition.value || ''}
                              onChange={(e) => {
                                handleFilterConditionUpdate(index, {
                                  ...condition,
                                  value: e.target.value
                                });
                              }}
                              placeholder="Value"
                              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                          )}

                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={() => handleFilterConditionRemove(index)}
                            className="text-red-500 hover:text-red-700 text-xs px-1"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      
                      <p className="mt-1 text-xs text-gray-500">
                        Only records matching these conditions will appear in the scanner list (e.g., Remark = "VIP")
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {!formData.hasOfflineLogic && (
        <p className="text-sm text-gray-500">
          Enable offline mode to allow scanning when internet connectivity is unavailable.
        </p>
      )}
    </div>
  );
};

export default OfflineLogicSection;